import { Badge } from "@/components/ui/badge";
import { format, parseISO } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ChevronDown, X, FileText, Sparkles, Eye, ScanLine, Save, Loader2, DollarSign, Check, ChevronsUpDown, MessageSquareCode } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import type { ExtractionData, ExtractionResult, LineItem } from "@prolarus/shared";

export type { ExtractionData, ExtractionResult, LineItem };

interface Vendor {
  id: string;
  name: string;
}

interface Props {
  results: ExtractionResult;
  onClear: () => void;
  onSave?: () => void;
  isSaving?: boolean;
  vendors?: Vendor[];
  selectedVendorId?: string | null;
  onVendorChange?: (vendorId: string | null) => void;
}

export function InvoiceExtractionResults({ 
  results, 
  onClear, 
  onSave, 
  isSaving,
  vendors = [],
  selectedVendorId,
  onVendorChange
}: Props) {
  const [showRawJson, setShowRawJson] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [vendorOpen, setVendorOpen] = useState(false);

  const getMethodBadge = (method: string) => {
    switch (method) {
      case 'native_text':
        return (
          <Badge variant="default" className="bg-green-600 hover:bg-green-700">
            <FileText className="w-3 h-3 mr-1" />
            Native Text
          </Badge>
        );
      case 'ocr':
        return (
          <Badge variant="default" className="bg-yellow-600 hover:bg-yellow-700">
            <ScanLine className="w-3 h-3 mr-1" />
            OCR
          </Badge>
        );
      case 'vision':
        return (
          <Badge variant="default" className="bg-orange-600 hover:bg-orange-700">
            <Eye className="w-3 h-3 mr-1" />
            Vision AI
          </Badge>
        );
      default:
        return <Badge variant="secondary">{method}</Badge>;
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 85) return 'bg-green-500';
    if (confidence >= 70) return 'bg-yellow-500';
    return 'bg-orange-500';
  };

  const formatCurrency = (amount: number | undefined, currency: string = 'USD') => {
    if (amount === undefined || amount === null) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return '-';
    try {
      return format(parseISO(dateStr), 'MMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  const { data } = results;
  const currency = data.currency || 'USD';
  const selectedVendor = vendors.find(v => v.id === selectedVendorId);

  return (
    <div className="space-y-6">
      {/* Header with extraction info */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 flex-wrap">
          {getMethodBadge(results.extraction_method)}
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Confidence:</span>
            <div className="flex items-center gap-2">
              <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                <div 
                  className={`h-full ${getConfidenceColor(results.confidence)} transition-all`}
                  style={{ width: `${results.confidence}%` }}
                />
              </div>
              <span className="text-sm font-medium">{results.confidence}%</span>
            </div>
          </div>
          <Badge variant="outline">
            {results.page_count} page{results.page_count !== 1 ? 's' : ''}
          </Badge>
          {results.debug?.processing_time_ms && (
            <span className="text-xs text-muted-foreground">
              {(results.debug.processing_time_ms / 1000).toFixed(1)}s
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onClear}>
            <X className="w-4 h-4 mr-1" />
            Clear
          </Button>
          {onSave && (
            <Button size="sm" onClick={onSave} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-1" />
                  Save Invoice
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Invoice Header Data */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Extracted Invoice Data
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Vendor on its own row */}
          <div className="mb-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Vendor / Service Provider</p>
            {vendors.length > 0 && onVendorChange ? (
              <Popover open={vendorOpen} onOpenChange={setVendorOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={vendorOpen}
                    className="w-full max-w-md justify-between h-auto min-h-[36px] py-1"
                  >
                    <span className="truncate text-left">
                      {selectedVendor ? selectedVendor.name : data.vendor_name || "Select vendor..."}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search vendors..." />
                    <CommandList>
                      <CommandEmpty>No vendor found.</CommandEmpty>
                      <CommandGroup>
                        {vendors.map((vendor) => (
                          <CommandItem
                            key={vendor.id}
                            value={vendor.name}
                            onSelect={() => {
                              onVendorChange(vendor.id === selectedVendorId ? null : vendor.id);
                              setVendorOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                selectedVendorId === vendor.id ? "opacity-100" : "opacity-0"
                              )}
                            />
                            {vendor.name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            ) : (
              <p className="font-medium">{data.vendor_name || '-'}</p>
            )}
            {selectedVendor && selectedVendor.name !== data.vendor_name && (
              <p className="text-xs text-muted-foreground mt-1">
                Extracted: {data.vendor_name}
              </p>
            )}
          </div>

          {/* Other fields in grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Invoice #</p>
              <p className="font-medium">{data.invoice_number || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Invoice Date</p>
              <p className="font-medium">{formatDate(data.invoice_date)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Due Date</p>
              <p className="font-medium">{formatDate(data.due_date)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Payment Terms</p>
              <p className="font-medium">{data.payment_terms || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Discount</p>
              <p className="font-medium">{data.discount != null ? `${data.discount}%` : '-'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Discount Expires</p>
              <p className="font-medium">{formatDate(data.discount_expiration)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Currency</p>
              <p className="font-medium">{currency}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Total</p>
              <p className="font-medium text-lg text-primary">
                {formatCurrency(data.total_amount, currency)}
              </p>
            </div>
          </div>

          {/* Totals Row */}
          <div className="mt-4 pt-4 border-t flex justify-end gap-8">
            <div className="text-right">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Subtotal</p>
              <p className="font-medium">{formatCurrency(data.subtotal, currency)}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Tax</p>
              <p className="font-medium">{formatCurrency(data.tax_amount, currency)}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Total</p>
              <p className="font-semibold text-lg">{formatCurrency(data.total_amount, currency)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Line Items */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">
            Line Items ({data.line_items?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">#</TableHead>
                  <TableHead className="w-28">Item Code</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right w-20">Qty</TableHead>
                  <TableHead className="text-right w-28">Unit Price</TableHead>
                  <TableHead className="w-20">UOM</TableHead>
                  <TableHead className="text-right w-28">Line Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.line_items?.length > 0 ? (
                  data.line_items.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-mono text-muted-foreground">
                        {item.sequence}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        <div className="flex items-center gap-1">
                          {item.item_code || '-'}
                          {item.price_book_item_id && (
                            <span title="Matched to price book">
                              <DollarSign className="w-3.5 h-3.5 text-green-600" />
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{item.description}</TableCell>
                      <TableCell className="text-right">{item.quantity}</TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(item.unit_price, currency)}
                      </TableCell>
                      <TableCell>{item.uom || '-'}</TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(item.line_total, currency)}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                      No line items extracted
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* AI Prompt Sent */}
      {results.prompt_sent && (
        <Collapsible open={showPrompt} onOpenChange={setShowPrompt}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="w-full justify-between">
              <span className="text-muted-foreground flex items-center gap-2">
                <MessageSquareCode className="w-4 h-4" />
                AI Prompt Sent
              </span>
              <ChevronDown className={`w-4 h-4 transition-transform ${showPrompt ? 'rotate-180' : ''}`} />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <Card className="mt-2">
              <CardContent className="p-4">
                <pre className="text-xs overflow-auto max-h-96 bg-muted p-4 rounded-lg whitespace-pre-wrap">
                  {results.prompt_sent}
                </pre>
              </CardContent>
            </Card>
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Raw JSON */}
      <Collapsible open={showRawJson} onOpenChange={setShowRawJson}>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="sm" className="w-full justify-between">
            <span className="text-muted-foreground">Raw JSON Response</span>
            <ChevronDown className={`w-4 h-4 transition-transform ${showRawJson ? 'rotate-180' : ''}`} />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <Card className="mt-2">
            <CardContent className="p-4">
              <pre className="text-xs overflow-auto max-h-96 bg-muted p-4 rounded-lg">
                {JSON.stringify(results, null, 2)}
              </pre>
            </CardContent>
          </Card>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
